import { AlertTriangle, Search, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { DeliveryDecision } from '~/components/common/delivery-run-panel';
import {
  AttributeGroupCard,
  isGroupLocked,
  labelForKey as resolveLabel,
  selectedAttributeKeys,
  type EnrichmentAttributeGroup,
  type EnrichmentRunPanelAction,
  type EnrichmentRunPanelState,
} from '~/components/common/enrichment-run-panel';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/utils/cn';

const REASON_MAX_LENGTH = 500;

interface Props {
  groups: EnrichmentAttributeGroup[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  state: EnrichmentRunPanelState;
  dispatch: (action: EnrichmentRunPanelAction) => void;
  decision: DeliveryDecision;
  onDecisionChange: (decision: DeliveryDecision) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  /** Extra decision content (e.g. the language approval section). The caller
   *  owns its state and folds it into whichever submit it wires up. */
  extraSection?: ReactNode;
  /** Retry delivery: the run was already approved, so there is no Approve / Reject choice. */
  isRedelivery?: boolean;
}

/**
 * Step 1 of the delivery wizard — the operator's Approve/Reject decision and,
 * on approval, which enriched attributes to deliver. Fully controlled: all
 * state (the attribute reducer, the decision, the rejection reason) lives in
 * `DeliveryWizardPage`, which also drives the shared stepper/footer from the
 * same state — this step is presentation only. Reuses the exact
 * attribute-picker building blocks the side-panel `DeliveryRunPanel` and the
 * creation `EnrichmentRunPanel` already share (`AttributeGroupCard`), so all
 * three surfaces stay visually and behaviourally in step.
 */
export function DeliveryWizardDecisionStep({
  groups,
  isLoading,
  isError,
  onRetry,
  state,
  dispatch,
  decision,
  onDecisionChange,
  notes,
  onNotesChange,
  extraSection,
  isRedelivery = false,
}: Props) {
  const { t } = useTranslation('common');
  const isApprove = decision === 'APPROVED';

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevDecisionRef = useRef(decision);
  useEffect(() => {
    if (prevDecisionRef.current !== decision) {
      prevDecisionRef.current = decision;
      if (decision === 'REJECTED') textareaRef.current?.focus();
    }
  }, [decision]);

  const query = state.search.trim().toLowerCase();
  const isSearching = query.length > 0;
  const visibleGroups = groups
    .map((group) => ({
      group,
      visibleAttributes: isSearching
        ? group.attributes.filter((a) => a.label.toLowerCase().includes(query))
        : group.attributes,
    }))
    .filter(({ visibleAttributes }) => visibleAttributes.length > 0);

  const selectedKeys = selectedAttributeKeys(state);
  const selectableGroups = groups.filter((g) => g.unavailable !== true);
  const allSelected =
    selectableGroups.length > 0 &&
    selectableGroups.every((g) =>
      g.singleSelect
        ? g.attributes.some((a) => state.selected[a.key])
        : g.attributes.every((a) => state.selected[a.key]),
    );

  return (
    <Card
      className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-0 overflow-hidden p-0"
      data-testid="delivery-wizard-step-panel-decision"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-5">
        <h2 className="text-lg font-semibold" data-testid="delivery-wizard-decision-title">
          {isRedelivery
            ? t('deliveryWizard.redeliver.decisionTitle')
            : t('deliveryRun.decision.label')}
        </h2>
        {/* Segmented control — replaces the two separate pill buttons, both
            sized to fill the row before, so a binary choice doesn't dominate
            the card. */}
        {isRedelivery ? (
          <p className="text-muted-foreground text-sm">
            {t('deliveryWizard.redeliver.decisionHint')}
          </p>
        ) : (
          <div
            role="radiogroup"
            aria-label={t('deliveryRun.decision.label')}
            data-testid="delivery-wizard-decision-radio-group"
            className="bg-muted/50 inline-flex gap-0.5 rounded-xl border p-1"
          >
            <button
              type="button"
              role="radio"
              aria-checked={isApprove}
              data-testid="delivery-wizard-decision-approve-radio"
              onClick={() => {
                onDecisionChange('APPROVED');
              }}
              className={cn(
                'inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors',
                isApprove
                  ? 'text-primary bg-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-4 items-center justify-center rounded-full border-2',
                  isApprove ? 'border-primary' : 'border-muted-foreground/40',
                )}
              >
                {isApprove && <span className="bg-primary size-1.5 rounded-full" />}
              </span>
              {t('deliveryRun.decision.approve')}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!isApprove}
              data-testid="delivery-wizard-decision-reject-radio"
              onClick={() => {
                onDecisionChange('REJECTED');
              }}
              className={cn(
                'inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors',
                !isApprove
                  ? 'text-destructive bg-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-4 items-center justify-center rounded-full border-2',
                  !isApprove ? 'border-destructive' : 'border-muted-foreground/40',
                )}
              >
                {!isApprove && <span className="bg-destructive size-1.5 rounded-full" />}
              </span>
              {t('deliveryRun.decision.reject')}
            </button>
          </div>
        )}
      </div>

      {isApprove ? (
        <div className="max-h-[65vh] flex-1 overflow-y-auto">
          <section
            className="space-y-3.5 border-b px-6 py-5"
            data-testid="delivery-wizard-attributes-section"
          >
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-foreground text-sm font-semibold">
                  {t('deliveryRun.attributes.title')}
                </h3>
                <div className="flex items-center gap-3">
                  <Button
                    variant="link"
                    size="sm"
                    disabled={isLoading || allSelected}
                    data-testid="delivery-wizard-attributes-select-all-button"
                    className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
                    onClick={() => {
                      dispatch({ type: 'SELECT_ALL' });
                    }}
                  >
                    {t('deliveryRun.attributes.selectAll')}
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    disabled={isLoading || selectedKeys.length === 0}
                    data-testid="delivery-wizard-attributes-clear-all-button"
                    className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
                    onClick={() => {
                      dispatch({ type: 'CLEAR_ALL' });
                    }}
                  >
                    {t('deliveryRun.attributes.clearAll')}
                  </Button>
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                {t('deliveryRun.attributes.description')}
              </p>
            </div>

            {isError ? (
              <div
                className="space-y-2.5 rounded-lg border px-4 py-4 text-center"
                data-testid="delivery-wizard-attributes-error"
              >
                <p className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
                  <AlertTriangle className="text-warning size-4 shrink-0" aria-hidden="true" />
                  {t('deliveryRun.attributes.error')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={onRetry}
                  data-testid="delivery-wizard-attributes-retry-button"
                >
                  {t('deliveryRun.attributes.retry')}
                </Button>
              </div>
            ) : isLoading ? (
              <div
                className="space-y-2.5"
                aria-busy="true"
                data-testid="delivery-wizard-attributes-loading"
              >
                {[0, 1, 2].map((i) => (
                  <div key={i} className="bg-muted/40 h-12 w-full animate-pulse rounded-lg" />
                ))}
              </div>
            ) : groups.length === 0 ? (
              <p
                className="text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm"
                data-testid="delivery-wizard-attributes-empty"
              >
                {t('deliveryRun.attributes.empty')}
              </p>
            ) : (
              <>
                <div className="relative">
                  <Search
                    aria-hidden="true"
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                  />
                  <Input
                    value={state.search}
                    placeholder={t('deliveryRun.attributes.searchPlaceholder')}
                    data-testid="delivery-wizard-attributes-search-input"
                    className="pr-9 pl-9"
                    onChange={(e) => {
                      dispatch({ type: 'SET_SEARCH', search: e.target.value });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && state.search) {
                        e.stopPropagation();
                        e.preventDefault();
                        dispatch({ type: 'SET_SEARCH', search: '' });
                      }
                    }}
                  />
                  {state.search && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('deliveryRun.attributes.clearSearch')}
                      data-testid="delivery-wizard-attributes-search-clear-button"
                      className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-7 w-7 -translate-y-1/2 cursor-pointer"
                      onClick={() => {
                        dispatch({ type: 'SET_SEARCH', search: '' });
                      }}
                    >
                      <X aria-hidden="true" className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>

                {visibleGroups.length === 0 ? (
                  <p
                    className="text-muted-foreground py-8 text-center text-sm"
                    data-testid="delivery-wizard-attributes-no-results"
                  >
                    {t('deliveryRun.attributes.noResults', { query: state.search })}
                  </p>
                ) : (
                  <div className="space-y-2.5" data-testid="delivery-wizard-attributes-list">
                    {visibleGroups.map(({ group, visibleAttributes }) => {
                      const locked = isGroupLocked(state, group);
                      return (
                        <AttributeGroupCard
                          key={group.slug}
                          group={group}
                          visibleAttributes={visibleAttributes}
                          selected={state.selected}
                          locked={locked}
                          requiresLabel={
                            group.requiresKey === undefined
                              ? null
                              : resolveLabel(groups, group.requiresKey)
                          }
                          expanded={isSearching || state.expanded[group.slug] !== false}
                          labelForKey={(key) => resolveLabel(groups, key)}
                          onToggleGroup={() => {
                            dispatch({ type: 'TOGGLE_GROUP', slug: group.slug });
                          }}
                          onToggleExpand={() => {
                            dispatch({ type: 'TOGGLE_EXPAND', slug: group.slug });
                          }}
                          onToggleAttribute={(key) => {
                            dispatch({ type: 'TOGGLE_ATTRIBUTE', key });
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </section>

          {extraSection}
        </div>
      ) : (
        // Reject: the card shrinks to this content — no attribute section, no
        // reserved empty space below it.
        <div className="px-6 py-5" data-testid="delivery-wizard-reject-section">
          <label htmlFor="delivery-wizard-reason" className="text-sm font-semibold">
            {t('deliveryRun.notes.rejectLabel')}
          </label>
          <p id="delivery-wizard-reason-help" className="text-muted-foreground mt-0.5 text-sm">
            {t('deliveryWizard.reject.helper')}
          </p>
          <Textarea
            id="delivery-wizard-reason"
            ref={textareaRef}
            data-testid="delivery-wizard-reject-reason-input"
            rows={4}
            maxLength={REASON_MAX_LENGTH}
            placeholder={t('deliveryRun.notes.rejectPlaceholder')}
            value={notes}
            aria-describedby="delivery-wizard-reason-help delivery-wizard-reason-count"
            className="focus-visible:border-destructive focus-visible:ring-destructive/20 mt-3 min-h-[112px] resize-y"
            onChange={(e) => {
              onNotesChange(e.target.value);
            }}
          />
          <div className="text-muted-foreground mt-1.5 flex items-center justify-between gap-3 text-xs">
            <span>{t('deliveryWizard.reject.keepsSelection', { count: selectedKeys.length })}</span>
            <span
              id="delivery-wizard-reason-count"
              className="shrink-0 tabular-nums"
              data-testid="delivery-wizard-reject-reason-count"
            >
              {t('deliveryWizard.reject.counter', {
                length: notes.length,
                max: REASON_MAX_LENGTH,
              })}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}
